/**
 * JOProClickCollectInjector — Mounts JOProClickCollect at the top of the
 * /services page via a React portal target, without touching the cockpit
 * ServicesPage.jsx source. Idempotent.
 *
 * Strategy : on every navigation, if we land on /services, find the first
 * <main> child of the public layout and prepend a host <div>. We mount the
 * <JOProClickCollect forceShow /> section into that host via createPortal.
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import JOProClickCollect from "./sections/JOProClickCollect";

const HOST_ID = "jo-procc-host";

export default function JOProClickCollectInjector() {
  const location = useLocation();
  const [host, setHost] = useState(null);

  useEffect(() => {
    // Cleanup previous host on any route change
    const prev = document.getElementById(HOST_ID);
    if (prev) prev.remove();
    setHost(null);

    if (location.pathname !== "/services") return;

    let cancelled = false;
    const attach = () => {
      if (cancelled) return true;
      // Find an anchor: the main content area on /services. We pick the
      // first <section> with [data-testid] starting with "services" if
      // present, otherwise the first <section> inside <main>.
      const main = document.querySelector("main") || document.body;
      if (!main) return false;
      // Insert host BEFORE the first child of <main> so the banner is
      // the very first thing visible after the header.
      let el = document.getElementById(HOST_ID);
      if (!el) {
        el = document.createElement("div");
        el.id = HOST_ID;
        main.insertBefore(el, main.firstChild);
      }
      setHost(el);
      return true;
    };

    if (attach()) return;
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      if (attach() || tries > 20) clearInterval(interval);
    }, 200);

    return () => {
      cancelled = true;
      clearInterval(interval);
      const el = document.getElementById(HOST_ID);
      if (el) el.remove();
    };
  }, [location.pathname]);

  if (!host) return null;
  return createPortal(<JOProClickCollect forceShow compact />, host);
}
